import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import styles from './DocumentPreview.module.scss';
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import cn from "classnames";
import { OfferPreview } from '~/components/offer-preview';
import { useModal } from '~/hooks';
import { postOfferUpload, removeOfferFile } from '../../../api/apiMethods';
import { message } from 'antd';
import { AttachmentsDetailsError, Offer } from '~/typings/types';
import { sanitizeData } from '~/utils';
import { AxiosError } from 'axios';
import { LoadingContainer } from '~/containers/loading-сontainer';

const ALLOWED_OFFER_TYPES = ['.pdf', 'application/pdf', '.doc', 'application/msword', '.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_OFFER_FILE_SIZE = 10 * 1024 * 1024; // 10 МБ

const validateOfferFile = (file: File): { isValid: boolean; errorMessage?: string } => {
	if (file.size === 0) {
		return { isValid: false, errorMessage: 'Файл пустой или поврежден' };
	}

	if (file.size > MAX_OFFER_FILE_SIZE) {
		const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
		return {
			isValid: false,
			errorMessage: `Размер файла (${fileSizeMB} МБ) превышает допустимый размер 10 МБ`,
		};
	}

	const fileName = file.name.toLowerCase();
	const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
	const fileMimeType = file.type.toLowerCase();

	const isTypeAllowed = ALLOWED_OFFER_TYPES.some(allowedType => {
		const normalized = allowedType.toLowerCase();
		if (normalized.startsWith('.')) return fileExtension === normalized;
		return fileMimeType === normalized;
	});

	if (!isTypeAllowed) {
		return {
			isValid: false,
			errorMessage: 'Недопустимый формат. Разрешенные форматы: DOC, DOCX, PDF',
		};
	}

	return { isValid: true };
};

const getOfferRequirementsText = () => {
	const extensions = ALLOWED_OFFER_TYPES
		.filter(t => t.startsWith('.'))
		.map(ext => ext.substring(1).toUpperCase())
		.join(', ');
	const maxSizeMB = (MAX_OFFER_FILE_SIZE / (1024 * 1024)).toFixed(0);
	return `${extensions} • до ${maxSizeMB} МБ`;
};

interface SelectDocumentPreviewProps {
	clientId: string;
	isUploadOffer: boolean;
	setIsUploadOffer: Dispatch<SetStateAction<boolean>>;
	errorUploadOffer: AttachmentsDetailsError;
	setErrorUploadOffer: Dispatch<AttachmentsDetailsError>;
	typeOffer: "custom" | "default";
	offer?: Offer;
	textLinkOffer: string;
	setTextLinkOffer: Dispatch<SetStateAction<string>>;
	setIsGeneratedOffer: Dispatch<SetStateAction<boolean>>;
}

export const DocumentPreview = ({ clientId, isUploadOffer, setIsUploadOffer, errorUploadOffer, setErrorUploadOffer, typeOffer, offer, textLinkOffer, setTextLinkOffer, setIsGeneratedOffer }: SelectDocumentPreviewProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { isShow, toggle } = useModal();
	const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
	const [isLoadingUploadedFile, setIsLoadingUploadedFile] = useState(false);

	const handleOptionClick = (option: 'upload' | 'generate') => {
		if (option === 'upload') {
			fileInputRef.current?.click();
		}

		if (option === 'generate') {
			toggle();
			handleRemoveFile();
		}
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const validation = validateOfferFile(file);
		if (!validation.isValid) {
			setErrorUploadOffer({ has: 1, message: validation.errorMessage || 'Ошибка валидации файла' });
			event.target.value = '';
			return;
		}

		const formData = new FormData();
		formData.append('file', file);
		formData.append('questionnaire_uid', clientId);
		setIsGeneratedOffer(false);
		setIsLoadingUploadedFile(true);

		try {
			setErrorUploadOffer(null);

			await postOfferUpload(formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			}, clientId);
			setIsUploadOffer(true);
			setUploadedFileName(sanitizeData(file.name));
		} catch (error) {
			const axiosError = error as AxiosError<{ message?: string }>;
			setErrorUploadOffer({ has: 1, message: sanitizeData(axiosError?.response?.data?.message || "Ошибка загрузки файла") });
		} finally {
			setIsLoadingUploadedFile(false);
		}

		event.target.value = '';
	};

	const handleRemoveFile = async () => {
		try {
			await removeOfferFile(clientId);
			setUploadedFileName(null);
			setIsUploadOffer(false);
		} catch (error) {
			message.error('Ошибка удаления файла');
		}
	};

	useEffect(() => {
		if (offer?.file_name) {
			setIsUploadOffer(true);
		}
	}, []);

	return (
		<>
			{typeOffer === 'custom' ? (
				<div className={styles.containerUpload}>
					<div className={styles.containerUploadActions}>
						<input
							className={styles.uploadLink}
							placeholder="Введите ссылку на оферту"
							value={textLinkOffer}
							onBlur={() => {
								if (isUploadOffer) {
									handleRemoveFile();
									setIsUploadOffer(false);
									setIsGeneratedOffer(false);
								}
							}}
							onChange={
								(e) => {
									setTextLinkOffer(e.target.value);
									setErrorUploadOffer(null);
								}
							}
						/>
						<span className={styles.orText}>или</span>
						<div
							className={cn(styles.uploadButton, {
								[styles.uploadButtonDisabled]: Boolean(textLinkOffer)
							})}
							onClick={() => handleOptionClick('upload')}
						>
							<LoadingContainer isLoading={isLoadingUploadedFile}>
								<UploadOutlined className={styles.uploadIcon} />
								<span className={styles.uploadItemText}>Загрузить файл</span>
								<span className={styles.uploadRequirements}>{getOfferRequirementsText()}</span>
							</LoadingContainer>
							<input
								type="file"
								accept={ALLOWED_OFFER_TYPES.join(',')}
								style={{ display: 'none' }}
								ref={fileInputRef}
								onChange={handleFileChange}
								disabled={Boolean(textLinkOffer)}
							/>
						</div>
					</div>
					{isUploadOffer && (
						<div className="file-info">
							<span id="file-name">{offer?.file_name || uploadedFileName}</span>
							<button type="button" id="remove-file" onClick={() => handleRemoveFile()} />
						</div>
					)}
				</div>

			) : (
				<>
					<div className={styles.generateButton} onClick={() => handleOptionClick('generate')}>
						<FileTextOutlined className={cn(styles.generateIcon)} />
						<span className={styles.uploadItemText}>Создать шаблон оферты с моими данными</span>
					</div>
					<OfferPreview isShow={isShow} toggle={toggle} clientId={clientId} setErrorUploadOffer={setErrorUploadOffer} setIsGeneratedOffer={setIsGeneratedOffer} />
				</>
			)}
		</>
	);
};